import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, CalendarClock, ShoppingBag, PawPrint, Heart, LogOut, Search, KeyRound, Trash2,
  Save, Dog, Droplets, Package, ChevronDown, ChevronUp, Home,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCustomer, customerApi, formatApiError } from "@/context/CustomerContext";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { MyBookingsDialog } from "@/components/MyBookingsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { normalizePhone, isValidGrPhone } from "@/lib/phone";
import { productName, productImg, AvailabilityBadge, PriceTag } from "@/pages/ShopPage";
import { formatPriceRange } from "@/lib/shop";

const inputCls = "rounded-xl border-brand/20 bg-white h-11 focus-visible:ring-brand";
const card = "bg-white rounded-3xl border border-brand/10 p-6 shadow-[0_8px_30px_rgba(134,77,99,0.06)]";

const ORDER_STATUS_STYLE = {
  new: "bg-sky-100 text-sky-800",
  preparing: "bg-blue-100 text-blue-800",
  awaiting_supplier: "bg-amber-100 text-amber-800",
  ready: "bg-bamboo-soft text-bamboo-deep",
  completed: "bg-brand-soft text-brand",
  cancelled: "bg-rose-100 text-rose-700",
};

export const OrderStatusBadge = ({ status, labels }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${ORDER_STATUS_STYLE[status] || "bg-stone-alt text-brand-ink"}`} data-testid={`order-status-${status}`}>
    {labels[status] || status}
  </span>
);

// ---------- Auth view (login / register / forgot + guest lookup) ----------
const AuthView = () => {
  const { t } = useLang();
  const a = t.account;
  const { login, register } = useCustomer();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else if (mode === "register") {
        await register(form);
      } else {
        await customerApi.post("/account/forgot-password", { email: form.email });
        setForgotSent(true);
        setBusy(false);
        return;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="account-auth">
      <div className="bg-white rounded-[2rem] border border-brand/10 shadow-[0_20px_60px_rgba(134,77,99,0.10)] overflow-hidden">
        <div className="bg-brand text-brand-cream px-7 pt-7 pb-6">
          <h2 className="font-heading text-3xl font-bold flex items-center gap-2">
            <User className="h-6 w-6" /> {a.title}
          </h2>
          <p className="text-brand-cream/80 text-sm mt-1">{a.subtitle}</p>
        </div>
        <div className="p-7">
          <div className="flex rounded-full bg-brand-soft/50 p-1 mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setForgotSent(false); }}
                className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors no-a11y-underline ${mode === m ? "bg-brand text-brand-cream" : "text-brand-ink/60"}`}
                data-testid={`auth-mode-${m}`}
              >
                {m === "login" ? a.login : a.register}
              </button>
            ))}
          </div>

          {forgotSent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <KeyRound className="h-10 w-10 mx-auto text-bamboo-deep mb-3" />
              <p className="text-sm text-brand-muted">{a.forgot_sent}</p>
              <Button variant="ghost" onClick={() => { setMode("login"); setForgotSent(false); }} className="mt-4 rounded-full text-brand font-bold" data-testid="forgot-back-login">
                {a.login}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {mode === "register" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.first_name}</Label>
                    <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="account-first-name" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.last_name}</Label>
                    <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="account-last-name" />
                  </div>
                </div>
              )}
              {mode !== "forgot" && null}
              <div>
                <Label className="text-brand-ink font-semibold">{a.email}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-1`} data-testid="account-email" />
              </div>
              {mode === "register" && (
                <div>
                  <Label className="text-brand-ink font-semibold">{a.phone}</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: normalizePhone(e.target.value).slice(0, 10) })}
                    inputMode="tel" maxLength={10} placeholder="69XXXXXXXX"
                    className={`${inputCls} mt-1 ${form.phone && !isValidGrPhone(form.phone) ? "border-rose-400" : ""}`}
                    data-testid="account-phone"
                  />
                  <p className="mt-1 text-xs text-brand-ink/50">{a.phone_hint}</p>
                </div>
              )}
              {mode !== "forgot" && (
                <div>
                  <Label className="text-brand-ink font-semibold">{a.password}</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} className={`${inputCls} mt-1`} data-testid="account-password" />
                </div>
              )}
              {mode === "forgot" && <p className="text-sm text-brand-muted">{a.forgot_body}</p>}
              <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12" data-testid="account-auth-submit">
                {mode === "login" ? (busy ? a.logging_in : a.login_btn) : mode === "register" ? (busy ? a.registering : a.register_btn) : a.forgot_btn}
              </Button>
              {mode === "login" && (
                <button onClick={() => setMode("forgot")} className="w-full text-center text-sm font-semibold text-brand hover:underline no-a11y-underline" data-testid="forgot-link">
                  {a.forgot_link}
                </button>
              )}
              {mode === "forgot" && (
                <button onClick={() => setMode("login")} className="w-full text-center text-sm font-semibold text-brand hover:underline no-a11y-underline" data-testid="back-login-link">
                  {a.login}
                </button>
              )}
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-brand/10 text-center">
            <button onClick={() => setGuestOpen(true)} className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:text-brand-hover no-a11y-underline" data-testid="guest-lookup-btn">
              <Search className="h-4 w-4" /> {a.guest_lookup}
            </button>
          </div>
        </div>
      </div>
      <MyBookingsDialog open={guestOpen} onOpenChange={setGuestOpen} />
    </div>
  );
};

// ---------- Order card with expandable history ----------
const OrderCard = ({ order, labels, lang }) => {
  const [open, setOpen] = useState(false);
  const a = labels;
  const fmtD = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };
  let deadline = null;
  if (order.status === "ready" && order.ready_deadline) {
    try {
      deadline = new Date(order.ready_deadline + "T00:00:00").toLocaleDateString(lang === "el" ? "el-GR" : "en-GB");
    } catch { deadline = order.ready_deadline; }
  }
  return (
    <div className={card} data-testid={`order-${order.order_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-brand" data-testid={`order-num-${order.order_number}`}>{order.order_number}</span>
            <OrderStatusBadge status={order.status} labels={a.order_status} />
            {order.coupon_code && (
              <span className="rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5">{order.coupon_discount} · {order.coupon_code}</span>
            )}
          </div>
          <p className="mt-1 text-xs text-brand-ink/50">{fmtD(order.created_at)}</p>
          {deadline && (
            <p className="mt-1 text-xs font-bold text-bamboo-deep" data-testid={`order-deadline-${order.order_number}`}>{a.ready_until}: {deadline}</p>
          )}
        </div>
        <p className="font-heading text-2xl font-bold text-brand-ink">€{order.total.toFixed(2)}</p>
      </div>
      <div className="mt-3 space-y-1">
        {order.items.map((i, k) => (
          <p key={k} className="text-sm text-brand-ink/75">
            {i.name_gr}{i.variant_label ? ` (${i.variant_label})` : ""} × {i.qty} — <span className="font-semibold">€{i.line_total.toFixed(2)}</span>
          </p>
        ))}
      </div>
      <button onClick={() => setOpen((o) => !o)} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand hover:text-brand-hover no-a11y-underline" data-testid={`order-history-toggle-${order.order_number}`}>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {a.history}
      </button>
      {open && (
        <div className="mt-3 rounded-2xl bg-brand-soft/30 border border-brand/10 p-4 space-y-2" data-testid={`order-history-${order.order_number}`}>
          {(order.status_history || []).map((h, k) => (
            <div key={k} className="flex items-center gap-2 text-xs text-brand-ink/70">
              <span className="font-mono">{fmtD(h.at)}</span>
              <span className="font-bold">{a.order_status[h.status] || h.status}</span>
              {h.note && <span className="italic text-brand-ink/50">— {h.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Main account page ----------
export default function AccountPage() {
  const { t, lang } = useLang();
  const a = t.account;
  const { customer, ready, logout, refresh, setCustomer } = useCustomer();
  const navigate = useNavigate();
  const [tab, setTab] = useState("home");
  const [overview, setOverview] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [orders, setOrders] = useState(null);
  const [pets, setPets] = useState(null);
  const [wishlist, setWishlist] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  // profile form
  const [profile, setProfile] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [delPw, setDelPw] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (customer) {
      setProfile({ first_name: customer.first_name || "", last_name: customer.last_name || "", email: customer.email || "", phone: customer.phone || "" });
    }
  }, [customer]);

  useEffect(() => {
    if (!customer) return;
    customerApi.get("/account/overview").then((r) => setOverview(r.data)).catch(() => {});
    customerApi.get("/account/bookings").then((r) => setBookings(r.data)).catch(() => {});
    customerApi.get("/account/orders").then((r) => setOrders(r.data)).catch(() => {});
    customerApi.get("/account/pets").then((r) => setPets(r.data)).catch(() => {});
    customerApi.get("/account/wishlist").then((r) => setWishlist(r.data.products)).catch(() => {});
  }, [customer]);

  const todayStr = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  }, []);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const res = await customerApi.put("/account/me", profile);
      setCustomer(res.data);
      toast.success(a.saved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePw = async () => {
    setBusy(true);
    try {
      await customerApi.post("/account/change-password", pwForm);
      setPwForm({ current_password: "", new_password: "" });
      toast.success(a.password_changed);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await customerApi.delete("/account/me", { data: { password: delPw } });
      toast.success(a.deleted);
      logout();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeWish = async (pid) => {
    try {
      await customerApi.delete(`/account/wishlist/${pid}`);
      setWishlist((w) => (w || []).filter((p) => p.id !== pid));
      refresh();
    } catch { /* noop */ }
  };

  if (!ready) return null;

  if (!customer) {
    return (
      <div className="min-h-screen bg-brand-soft/30">
        <Seo title={t.seo.account_title} path="/account" noindex />
        <Navbar />
        <main className="pt-28 pb-24 px-5">
          <AuthView />
        </main>
        <Footer />
      </div>
    );
  }

  const TABS = [
    { id: "home", label: a.tab_home, icon: Home },
    { id: "bookings", label: a.tab_bookings, icon: CalendarClock },
    { id: "orders", label: a.tab_orders, icon: ShoppingBag },
    { id: "profile", label: a.tab_profile, icon: User },
    { id: "pets", label: a.tab_pets, icon: PawPrint },
    { id: "wishlist", label: a.tab_wishlist, icon: Heart },
  ];

  const allBookings = bookings
    ? [
        ...bookings.grooming.map((b) => ({ ...b, _type: "grooming", _date: b.preferred_date, _time: b.preferred_time })),
        ...bookings.selfwash.map((b) => ({ ...b, _type: "selfwash", _date: b.date, _time: b.time })),
      ].sort((x, y) => (y._date + y._time).localeCompare(x._date + x._time))
    : [];
  const upcoming = allBookings.filter((b) => b._date >= todayStr && b.status === "confirmed");
  const past = allBookings.filter((b) => !(b._date >= todayStr && b.status === "confirmed"));

  const BookingRow = ({ b }) => (
    <div className={`rounded-2xl border p-4 ${b._type === "grooming" ? "bg-brand-soft/40 border-brand/15" : "bg-bamboo-soft/40 border-bamboo/20"}`} data-testid={`account-booking-${b.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${b._type === "grooming" ? "bg-brand text-brand-cream" : "bg-bamboo text-brand-cream"}`}>
          {b._type === "grooming" ? <Dog className="h-3 w-3" /> : <Droplets className="h-3 w-3" />}
          {b._type === "grooming" ? "Grooming" : "Self-Service"}
        </span>
        <span className="font-bold text-brand-ink font-mono text-sm">{b._date} · {b._time}</span>
        <span className="text-xs font-bold text-brand-ink/50 uppercase">{b.status}</span>
      </div>
      <p className="mt-1.5 text-sm text-brand-ink/70">
        {b._type === "grooming" ? `🐾 ${b.pet_name}${b.pet_breed ? ` (${b.pet_breed})` : ""}` : `🛁 ${b.plan} · €${b.total_price ?? b.plan_price}`}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-soft/30">
      <Seo title={t.seo.account_title} path="/account" noindex />
      <Navbar />
      <main className="pt-28 pb-24 px-5 sm:px-8 max-w-5xl mx-auto" data-testid="account-page">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-brand-ink" data-testid="account-welcome">
              {a.welcome}, {customer.first_name}
            </h1>
            <p className="mt-1 text-sm text-brand-ink/50">{a.autolink_note}</p>
          </div>
          <Button onClick={() => { logout(); navigate("/"); }} variant="outline" className="rounded-full border-brand/30 text-brand font-bold" data-testid="account-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> {a.logout}
          </Button>
        </div>

        <div className="mt-8 flex gap-2 overflow-x-auto pb-1" data-testid="account-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold whitespace-nowrap transition-colors no-a11y-underline ${
                tab === tb.id ? "bg-brand text-brand-cream" : "bg-white text-brand-ink/70 border border-brand/10 hover:bg-brand-soft/50"
              }`}
              data-testid={`account-tab-${tb.id}`}
            >
              <tb.icon className="h-4 w-4" /> {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "home" && (
            <div className="grid gap-5 md:grid-cols-2" data-testid="account-home">
              <div className={card}>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-brand" /> {a.next_booking}
                </p>
                {overview?.next_booking ? (
                  <div className="mt-3" data-testid="overview-next-booking">
                    <p className="font-heading text-2xl font-bold text-brand-ink">
                      {overview.next_booking.date} · {overview.next_booking.time}
                    </p>
                    <p className="text-sm text-brand-ink/60 mt-1">
                      {overview.next_booking.type === "grooming" ? `Grooming${overview.next_booking.pet_name ? ` · ${overview.next_booking.pet_name}` : ""}` : `Self-Service · ${overview.next_booking.plan}`}
                    </p>
                  </div>
                ) : (
                  <p className="mt-3 text-brand-ink/50" data-testid="overview-no-booking">{a.no_upcoming}</p>
                )}
                <Button onClick={() => navigate("/#grooming")} className="mt-4 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="overview-new-booking">
                  {a.new_booking_btn}
                </Button>
              </div>
              <div className={card}>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 flex items-center gap-2">
                  <Package className="h-4 w-4 text-brand" /> {a.last_order}
                </p>
                {overview?.last_order ? (
                  <div className="mt-3" data-testid="overview-last-order">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-brand">{overview.last_order.order_number}</span>
                      <OrderStatusBadge status={overview.last_order.status} labels={a.order_status} />
                    </div>
                    <p className="text-sm text-brand-ink/60 mt-1">
                      {overview.last_order.items.length} {a.items} · €{overview.last_order.total.toFixed(2)}
                    </p>
                  </div>
                ) : (
                  <p className="mt-3 text-brand-ink/50" data-testid="overview-no-order">{a.no_orders}</p>
                )}
                <Button onClick={() => navigate("/shop")} variant="outline" className="mt-4 rounded-full border-brand/30 text-brand font-bold" data-testid="overview-go-shop">
                  {a.go_shop}
                </Button>
              </div>
            </div>
          )}

          {tab === "bookings" && (
            <div className="space-y-5" data-testid="account-bookings">
              <Button onClick={() => setManageOpen(true)} variant="outline" className="rounded-full border-brand/30 text-brand font-bold" data-testid="manage-bookings-btn">
                <CalendarClock className="h-4 w-4 mr-2" /> {a.manage_bookings}
              </Button>
              {allBookings.length === 0 ? (
                <div className={`${card} text-center text-brand-ink/50`} data-testid="bookings-empty">{a.bookings_empty}</div>
              ) : (
                <>
                  {upcoming.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 mb-2">{a.upcoming}</p>
                      <div className="space-y-3">{upcoming.map((b) => <BookingRow key={`${b._type}-${b.id}`} b={b} />)}</div>
                    </div>
                  )}
                  {past.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 mb-2">{a.past}</p>
                      <div className="space-y-3">{past.map((b) => <BookingRow key={`${b._type}-${b.id}`} b={b} />)}</div>
                    </div>
                  )}
                </>
              )}
              <MyBookingsDialog open={manageOpen} onOpenChange={setManageOpen} initialPhone={customer.phone} />
            </div>
          )}

          {tab === "orders" && (
            <div className="space-y-4" data-testid="account-orders">
              {orders === null ? null : orders.length === 0 ? (
                <div className={`${card} text-center text-brand-ink/50`} data-testid="orders-empty">{a.orders_empty}</div>
              ) : (
                orders.map((o) => <OrderCard key={o.id} order={o} labels={a} lang={lang} />)
              )}
            </div>
          )}

          {tab === "profile" && (
            <div className="space-y-5" data-testid="account-profile">
              <div className={card}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.first_name}</Label>
                    <Input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="profile-first-name" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.last_name}</Label>
                    <Input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className={`${inputCls} mt-1`} data-testid="profile-last-name" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.email}</Label>
                    <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={`${inputCls} mt-1`} data-testid="profile-email" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.phone}</Label>
                    <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: normalizePhone(e.target.value).slice(0, 10) })} inputMode="tel" maxLength={10} className={`${inputCls} mt-1`} data-testid="profile-phone" />
                  </div>
                </div>
                <Button onClick={saveProfile} disabled={busy} className="mt-5 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="profile-save-btn">
                  <Save className="h-4 w-4 mr-2" /> {busy ? a.saving : a.save}
                </Button>
              </div>

              <div className={card}>
                <p className="font-heading text-xl font-bold text-brand-ink flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-brand" /> {a.change_password}
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.current_password}</Label>
                    <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className={`${inputCls} mt-1`} data-testid="pw-current" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{a.new_password}</Label>
                    <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className={`${inputCls} mt-1`} data-testid="pw-new" />
                  </div>
                </div>
                <Button onClick={changePw} disabled={busy || !pwForm.current_password || !pwForm.new_password} variant="outline" className="mt-4 rounded-full border-brand/30 text-brand font-bold" data-testid="pw-change-btn">
                  {a.change_password}
                </Button>
              </div>

              <div className={`${card} border-rose-200`}>
                <p className="font-heading text-xl font-bold text-rose-700 flex items-center gap-2">
                  <Trash2 className="h-5 w-5" /> {a.delete_account}
                </p>
                <p className="mt-2 text-sm text-brand-muted">{a.delete_body}</p>
                {delOpen ? (
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <Input type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} placeholder={a.delete_password_ph} className={`${inputCls} flex-1`} data-testid="delete-password" />
                    <Button onClick={deleteAccount} disabled={busy || !delPw} variant="outline" className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold" data-testid="delete-confirm-btn">
                      {a.delete_confirm_btn}
                    </Button>
                  </div>
                ) : (
                  <Button onClick={() => setDelOpen(true)} variant="outline" className="mt-4 rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold" data-testid="delete-open-btn">
                    {a.delete_account}
                  </Button>
                )}
              </div>
            </div>
          )}

          {tab === "pets" && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="account-pets">
              {pets === null ? null : pets.length === 0 ? (
                <div className={`${card} sm:col-span-2 text-center text-brand-ink/50`} data-testid="pets-empty">{a.pets_empty}</div>
              ) : (
                pets.map((p, i) => (
                  <div key={i} className={`${card} flex items-center gap-4`} data-testid={`pet-${p.name}`}>
                    <span className="grid place-items-center h-14 w-14 rounded-full bg-brand-soft text-brand shrink-0">
                      <Dog className="h-7 w-7" />
                    </span>
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-brand-ink">{p.name}</h3>
                      <p className="text-sm text-brand-ink/60">
                        {[p.breed, p.size, `${p.visits} ${a.visits}`].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "wishlist" && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="account-wishlist">
              {wishlist === null ? null : wishlist.length === 0 ? (
                <div className={`${card} sm:col-span-2 lg:col-span-3 text-center text-brand-ink/50`} data-testid="wishlist-empty">{a.wishlist_empty}</div>
              ) : (
                wishlist.map((p) => (
                  <div key={p.id} className="bg-white rounded-3xl border border-brand/10 overflow-hidden" data-testid={`wishlist-${p.id}`}>
                    <button onClick={() => navigate(`/shop/product/${p.id}`)} className="block w-full no-a11y-underline">
                      <div className="aspect-square bg-stone-alt/50">
                        {productImg(p) ? (
                          <img src={productImg(p)} alt={productName(p, lang)} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full grid place-items-center text-brand/20"><PawPrint className="h-10 w-10" /></div>
                        )}
                      </div>
                    </button>
                    <div className="p-4">
                      <h3 className="font-heading text-lg font-bold text-brand-ink truncate">{productName(p, lang)}</h3>
                      <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                        <PriceTag p={p} />
                        <AvailabilityBadge availability={p.availability} lead={p.lead_days} />
                      </div>
                      <Button onClick={() => removeWish(p.id)} variant="ghost" size="sm" className="mt-2 rounded-full text-rose-500 hover:bg-rose-50 text-xs font-bold" data-testid={`wishlist-remove-${p.id}`}>
                        <Heart className="h-3.5 w-3.5 mr-1 fill-rose-500" /> {t.shop.wishlist_remove}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
